/**
 * Kampagnens varer fordelt på varetype ([D-509], Charlie 01-11-2026).
 *
 * Afsnittet under Produkt-regler på kampagnens side: pr. varetype antal
 * produkter, som reglerne giver kampagnepris, delt i 3 kolonner, der
 * udelukker hinanden:
 *   - Fast lav pris: produktet har WP-kategorien Fast lav pris (5938), læst i
 *     BC's PRIMÆRKATEGORI (Bornerups itemSubcategories), så også varer uden for
 *     shoppen tæller. Markøren vinder over de 2 andre.
 *   - Normalpris: kampagnen er ikke slået igennem: BC's salgsprisliste har ingen
 *     aktiv rabatlinje på mindst kampagnens sats for varen.
 *   - Tilbudsvare: resten.
 * Original og -R-kopi tæller som 1 produkt; bundles står i deres egen række.
 * Antallet i BC står først og antallet i shoppen (WebShop Action Active) i
 * parentes; tooltippen viser også udgivet i WP og Active med salgbart lager.
 */

export const CAMPAIGN_TYPE_COUNT_COLUMNS = ['normal', 'tilbud', 'flp'] as const;
export type CampaignTypeCountColumn = (typeof CAMPAIGN_TYPE_COUNT_COLUMNS)[number];

export const CAMPAIGN_TYPE_COUNT_COLUMN_LABELS: Record<CampaignTypeCountColumn, string> = {
  normal: 'Normalpris',
  tilbud: 'Tilbudsvare',
  flp: 'Fast lav pris',
};

/**
 * Varetyperne. Rækkefølgen afgør, når et produkt ligger under flere: den
 * første vinder. wpCategoryId = WP's hovedkategori (Fliser på topniveau, de
 * øvrige under Badeværelse 1217). bcGroups = præfikser på BC's produkttype
 * (bogføringsgruppen), brugt når produktet hverken er i WP eller har en
 * PRIMÆRKATEGORI i BC.
 */
export const CAMPAIGN_ITEM_TYPES = [
  { key: 'fliser', label: 'Fliser', wpCategoryId: 1216, bcGroups: ['FLI', 'MOS', 'NAT', 'TER'] },
  { key: 'sanitet', label: 'Sanitet', wpCategoryId: 135, bcGroups: ['TOILET', 'VASKE', 'VVS', 'BADEKAR', 'BRUSSÆT', 'AFSKÆRM'] },
  { key: 'moebler', label: 'Badeværelsesmøbler', wpCategoryId: 172, bcGroups: ['MØBLER'] },
  { key: 'tilbehoer', label: 'Badeværelsestilbehør', wpCategoryId: 143, bcGroups: ['TILBEHØ', 'HÅKLTØ'] },
  { key: 'armaturer', label: 'Armaturer', wpCategoryId: 141, bcGroups: ['ARMATUR'] },
  { key: 'spejle', label: 'Spejle', wpCategoryId: 136, bcGroups: ['SPEJLE'] },
] as const;

export type CampaignItemTypeKey = (typeof CAMPAIGN_ITEM_TYPES)[number]['key'] | 'oevrige' | 'bundles';

export const CAMPAIGN_ITEM_TYPE_LABELS: Record<CampaignItemTypeKey, string> = {
  ...(Object.fromEntries(CAMPAIGN_ITEM_TYPES.map((t) => [t.key, t.label])) as Record<(typeof CAMPAIGN_ITEM_TYPES)[number]['key'], string>),
  oevrige: 'Øvrige',
  bundles: 'Bundles',
};

/** Varetypen efter BC's produkttype (fx FLISER_EX → fliser); null = ingen af typerne. */
export function campaignItemTypeOfGroup(postingGroup: string | null | undefined): CampaignItemTypeKey | null {
  const g = (postingGroup ?? '').trim().toUpperCase();
  if (!g) return null;
  for (const t of CAMPAIGN_ITEM_TYPES) if (t.bcGroups.some((p) => g.startsWith(p))) return t.key;
  return null;
}

/** Varetypen efter WP-kategorierne (direkte eller via træet); null = ingen af typerne. */
export function campaignItemTypeOfCategories(
  categoryIds: readonly number[],
  parentOf: (id: number) => number | undefined,
): CampaignItemTypeKey | null {
  const ids = new Set<number>();
  for (const c of categoryIds) {
    let cur: number | undefined = c;
    for (let i = 0; cur && i < 20; i++, cur = parentOf(cur)) ids.add(cur);
  }
  for (const t of CAMPAIGN_ITEM_TYPES) if (ids.has(t.wpCategoryId)) return t.key;
  return null;
}

export interface CampaignTypeCountCell {
  /** Produkter i BC (original og -R-kopi som 1). */
  bc: number;
  /** Heraf i shoppen: WebShop Action Active på originalen eller kopien. */
  active: number;
  /** Heraf udgivet i WP (publish). */
  wp: number;
  /** Heraf Active med salgbart lager (disponibelt på varekortet eller webshoppens eget tal). */
  activeInStock: number;
  /** Varenumrene bag cellen (original og kopi), så draweren kan filtrere. */
  items: string[];
}

/**
 * Rækkens vej til WP (Charlie 01-11-2026), på tværs af de 3 kolonner, 1 pr.
 * produkt. Trinene er indeholdt i hinanden: korrekt pris ⊂ udgivet ⊂ indlæst
 * ⊂ klar ⊂ bør på WP.
 */
export interface CampaignTypeWpFunnel {
  /** Kampagnens produkter der ikke er spærret i BC (Blocked eller Sales Blocked): de bør på WP. */
  shouldWp: number;
  /** Kampagnens produkter der er spærret i BC (ikke med i shouldWp). */
  blocked: number;
  /** Heraf klar til WP: WebShop Action Active på et varenummer der ikke er spærret. */
  ready: number;
  /** Af de klar: findes i WP (enhver status undtagen papirkurven). */
  loaded: number;
  /** Af de indlæste: udgivet i WP. */
  published: number;
  /**
   * Af de udgivne: WP's tilbudspris giver mindst kampagnens rabat (minus et
   * halvt procentpoint) af WP's normalpris, og tilbuddets datoer i WP dækker
   * kampagnens periode.
   */
  correctPrice: number;
}

export function emptyCampaignTypeWpFunnel(): CampaignTypeWpFunnel {
  return { shouldWp: 0, blocked: 0, ready: 0, loaded: 0, published: 0, correctPrice: 0 };
}

export interface CampaignTypeCountRow {
  /** Varetypen (CampaignItemTypeKey) eller matrixens gruppe (fx kk-vaske). */
  key: string;
  label: string;
  /** Det fulde navn, når label er forkortet (matrixens grupper). */
  title?: string;
  cells: Record<CampaignTypeCountColumn, CampaignTypeCountCell>;
  /** Vejen til WP; mangler i optællinger fra før 01-11-2026. */
  wp?: CampaignTypeWpFunnel;
}

export interface CampaignTypeCounts {
  rows: CampaignTypeCountRow[];
  /**
   * Samme produkter fordelt på kampagneplanens rabatmatrix (leverandør og BC's
   * produkttype, "Marginalen bag matrixen"); null = kampagnen har ingen matrix.
   */
  matrixRows?: CampaignTypeCountRow[] | null;
  /** Hvor varetypen kom fra (produkter, bundles ikke medregnet). */
  typeSource: { wp: number; bcPath: number; bcGroup: number; none: number };
  /** WP-kategoriens id for Fast lav pris (null = ikke fundet i WP, kolonnen er da 0). */
  flpCategoryId: number | null;
  /** Rabatlinjer læst i BC's salgsprislister (aktive i dag, kvalificerende). */
  discountLines: number;
}

export function emptyCampaignTypeCountCell(): CampaignTypeCountCell {
  return { bc: 0, active: 0, wp: 0, activeInStock: 0, items: [] };
}
